/**
 * Global Modal/Dialog Configuration
 *
 * Centralized configuration for modal dialogs, overlays, and popups across the application.
 * This ensures consistent modal behavior and appearance throughout all components.
 */

import type { CSSProperties } from 'react';
import { formConfig } from '../forms/form-config';

export interface ModalConfig {
  /** Overlay/backdrop configurations */
  overlay: {
    default: CSSProperties;
    blur: CSSProperties;
    dark: CSSProperties;
    light: CSSProperties;
    transparent: CSSProperties;
  };
  /** Modal container configurations */
  container: {
    default: CSSProperties;
    centered: CSSProperties;
    top: CSSProperties;
    bottom: CSSProperties;
    left: CSSProperties;
    right: CSSProperties;
  };
  /** Modal size variants */
  sizes: {
    small: CSSProperties;
    medium: CSSProperties;
    large: CSSProperties;
    xlarge: CSSProperties;
    fullscreen: CSSProperties;
    auto: CSSProperties;
  };
  /** Modal header configurations */
  header: {
    default: CSSProperties;
    withIcon: CSSProperties;
    withClose: CSSProperties;
    title: CSSProperties;
    subtitle: CSSProperties;
  };
  /** Modal body configurations */
  body: {
    default: CSSProperties;
    scrollable: CSSProperties;
    padded: CSSProperties;
    noPadding: CSSProperties;
  };
  /** Modal footer configurations */
  footer: {
    default: CSSProperties;
    actions: CSSProperties;
    centered: CSSProperties;
    spaceBetween: CSSProperties;
  };
  /** Modal type-specific configurations */
  types: {
    alert: {
      container: CSSProperties;
      icon: CSSProperties;
    };
    confirm: {
      container: CSSProperties;
      message: CSSProperties;
      actions: CSSProperties;
    };
    form: {
      container: CSSProperties;
      fieldWrapper: CSSProperties;
    };
    custom: {
      container: CSSProperties;
    };
    drawer: {
      container: CSSProperties;
      handle: CSSProperties;
    };
    bottomSheet: {
      container: CSSProperties;
      dragHandle: CSSProperties;
    };
  };
  /** Animation configurations */
  animation: {
    overlayEnter: number;
    overlayExit: number;
    modalEnter: number;
    modalExit: number;
    scaleFrom: number;
    scaleTo: number;
    slideDistance: string;
    easing: string;
  };
  /** Accessibility configurations */
  accessibility: {
    focusTrapEnabled: boolean;
    escapeKeyClose: boolean;
    overlayClickClose: boolean;
    autoFocusFirst: boolean;
    restoreFocus: boolean;
    announceToScreenReader: boolean;
  };
  /** Z-index layering */
  zIndex: {
    overlay: number;
    modal: number;
    modalStacked: number;
    dropdown: number;
    tooltip: number;
  };
  /** Button configurations (uses form config) */
  buttons: {
    primary: {
      className: string;
      styles: CSSProperties;
      loadingClass: string;
    };
    secondary: {
      className: string;
      styles: CSSProperties;
      loadingClass: string;
    };
    danger: {
      className: string;
      styles: CSSProperties;
      loadingClass: string;
    };
  };
}

export const modalConfig: ModalConfig = {
  // Overlay/backdrop configurations
  overlay: {
    default: {
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9998,
      transition: 'opacity 200ms ease-out',
    },
    blur: {
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
      backdropFilter: 'blur(4px)',
      WebkitBackdropFilter: 'blur(4px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9998,
    },
    dark: {
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.75)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9998,
    },
    light: {
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9998,
    },
    transparent: {
      position: 'fixed',
      inset: 0,
      backgroundColor: 'transparent',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9998,
      pointerEvents: 'none',
    },
  },

  // Modal container configurations
  container: {
    default: {
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      display: 'flex',
      flexDirection: 'column',
      maxHeight: '90vh',
      margin: '16px',
      position: 'relative',
      zIndex: 9999,
      transition: 'all 200ms ease-out',
    },
    centered: {
      position: 'relative',
      margin: 'auto',
    },
    top: {
      position: 'relative',
      marginTop: '5vh',
      marginBottom: 'auto',
    },
    bottom: {
      position: 'relative',
      marginTop: 'auto',
      marginBottom: '5vh',
    },
    left: {
      position: 'fixed',
      left: 0,
      top: 0,
      height: '100vh',
      margin: 0,
      borderRadius: '0 12px 12px 0',
      maxHeight: '100vh',
    },
    right: {
      position: 'fixed',
      right: 0,
      top: 0,
      height: '100vh',
      margin: 0,
      borderRadius: '12px 0 0 12px',
      maxHeight: '100vh',
    },
  },

  // Modal size variants
  sizes: {
    small: {
      width: '100%',
      maxWidth: '400px',
    },
    medium: {
      width: '100%',
      maxWidth: '600px',
    },
    large: {
      width: '100%',
      maxWidth: '800px',
    },
    xlarge: {
      width: '100%',
      maxWidth: '1200px',
    },
    fullscreen: {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      margin: 0,
      borderRadius: 0,
    },
    auto: {
      width: 'auto',
      maxWidth: '90vw',
    },
  },

  // Modal header configurations
  header: {
    default: {
      padding: '24px 24px 0',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      borderBottom: '1px solid #e5e7eb',
      marginBottom: '24px',
    },
    withIcon: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    withClose: {
      paddingRight: '48px',
      position: 'relative',
    },
    title: {
      fontSize: '20px',
      fontWeight: '600',
      lineHeight: '28px',
      color: '#111827',
      margin: 0,
    },
    subtitle: {
      fontSize: '14px',
      fontWeight: '400',
      lineHeight: '20px',
      color: '#6b7280',
      marginTop: '4px',
    },
  },

  // Modal body configurations
  body: {
    default: {
      padding: '0 24px 24px',
      flex: 1,
      overflowY: 'auto',
      overflowX: 'hidden',
    },
    scrollable: {
      padding: '0 24px 24px',
      flex: 1,
      overflowY: 'auto',
      overflowX: 'hidden',
      maxHeight: 'calc(90vh - 200px)',
    },
    padded: {
      padding: '24px',
    },
    noPadding: {
      padding: 0,
    },
  },

  // Modal footer configurations
  footer: {
    default: {
      padding: '24px',
      borderTop: '1px solid #e5e7eb',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    actions: {
      display: 'flex',
      gap: '12px',
      justifyContent: 'flex-end',
      width: '100%',
    },
    centered: {
      justifyContent: 'center',
    },
    spaceBetween: {
      justifyContent: 'space-between',
    },
  },

  // Modal type-specific configurations
  types: {
    alert: {
      container: {
        maxWidth: '450px',
        textAlign: 'center',
      },
      icon: {
        width: '48px',
        height: '48px',
        margin: '0 auto 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
      },
    },
    confirm: {
      container: {
        maxWidth: '500px',
      },
      message: {
        fontSize: '16px',
        lineHeight: '24px',
        color: '#374151',
        marginBottom: '24px',
      },
      actions: {
        display: 'flex',
        gap: '12px',
        justifyContent: 'flex-end',
      },
    },
    form: {
      container: {
        maxWidth: '700px',
      },
      fieldWrapper: {
        marginBottom: '20px',
      },
    },
    custom: {
      container: {
        // Custom modals have no default constraints
      },
    },
    drawer: {
      container: {
        height: '100vh',
        maxHeight: '100vh',
        borderRadius: 0,
      },
      handle: {
        width: '4px',
        height: '32px',
        backgroundColor: '#d1d5db',
        borderRadius: '2px',
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        cursor: 'ew-resize',
      },
    },
    bottomSheet: {
      container: {
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        borderRadius: '16px 16px 0 0',
        maxHeight: '90vh',
      },
      dragHandle: {
        width: '32px',
        height: '4px',
        backgroundColor: '#d1d5db',
        borderRadius: '2px',
        margin: '8px auto',
        cursor: 'ns-resize',
      },
    },
  },

  // Animation configurations
  animation: {
    overlayEnter: 200,
    overlayExit: 150,
    modalEnter: 200,
    modalExit: 150,
    scaleFrom: 0.95,
    scaleTo: 1,
    slideDistance: '20px',
    easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
  },

  // Accessibility configurations
  accessibility: {
    focusTrapEnabled: true,
    escapeKeyClose: true,
    overlayClickClose: true,
    autoFocusFirst: true,
    restoreFocus: true,
    announceToScreenReader: true,
  },

  // Z-index layering
  zIndex: {
    overlay: 9998,
    modal: 9999,
    modalStacked: 10000,
    dropdown: 10001,
    tooltip: 10002,
  },

  // Button configurations (uses form config for consistency)
  buttons: {
    primary: {
      className: (formConfig.button.primary as any).className ?? '',
      styles: formConfig.button.primary as any,
      loadingClass: (formConfig.button.primary as any).loadingClass ?? '',
    },
    secondary: {
      className: (formConfig.button.secondary as any).className ?? '',
      styles: formConfig.button.secondary as any,
      loadingClass: (formConfig.button.secondary as any).loadingClass ?? '',
    },
    danger: {
      className: (formConfig.button.danger as any).className ?? '',
      styles: formConfig.button.danger as any,
      loadingClass: (formConfig.button.danger as any).loadingClass ?? '',
    },
  },
};

/**
 * Utility function to get modal overlay styles
 */
export function getOverlayStyles(
  variant: 'default' | 'blur' | 'dark' | 'light' | 'transparent' = 'default',
  isAnimating: boolean = false,
  animationState: 'entering' | 'entered' | 'exiting' | 'exited' = 'entered'
): CSSProperties {
  const styles = { ...modalConfig.overlay[variant] };

  if (isAnimating) {
    switch (animationState) {
      case 'entering':
        styles.opacity = 0;
        break;
      case 'entered':
        styles.opacity = 1;
        break;
      case 'exiting':
        styles.opacity = 0;
        break;
      case 'exited':
        styles.opacity = 0;
        styles.pointerEvents = 'none';
        break;
    }
  }

  return styles;
}

/**
 * Utility function to get modal container styles
 */
export function getModalStyles(
  size: keyof typeof modalConfig.sizes = 'medium',
  position: 'default' | 'centered' | 'top' | 'bottom' | 'left' | 'right' = 'centered',
  type: keyof typeof modalConfig.types = 'custom',
  isAnimating: boolean = false,
  animationState: 'entering' | 'entered' | 'exiting' | 'exited' = 'entered'
): CSSProperties {
  const styles = {
    ...modalConfig.container.default,
    ...modalConfig.container[position],
    ...modalConfig.sizes[size],
    ...modalConfig.types[type].container,
  };

  if (isAnimating) {
    const { scaleFrom, scaleTo, slideDistance } = modalConfig.animation;
    
    switch (animationState) {
      case 'entering':
        styles.transform = `scale(${scaleFrom}) translateY(-${slideDistance})`;
        styles.opacity = 0;
        break;
      case 'entered':
        styles.transform = `scale(${scaleTo}) translateY(0)`;
        styles.opacity = 1;
        break;
      case 'exiting':
        styles.transform = `scale(${scaleFrom}) translateY(-${slideDistance})`;
        styles.opacity = 0;
        break;
      case 'exited':
        styles.transform = `scale(${scaleFrom}) translateY(-${slideDistance})`;
        styles.opacity = 0;
        styles.pointerEvents = 'none';
        break;
    }
  }

  return styles;
}

/**
 * CSS class names for modal styling
 */
export const modalClasses = {
  // Overlay classes
  overlay: 'app-modal-overlay',
  overlayBlur: 'app-modal-overlay--blur',
  overlayDark: 'app-modal-overlay--dark',
  overlayLight: 'app-modal-overlay--light',
  overlayTransparent: 'app-modal-overlay--transparent',
  
  // Container classes
  modal: 'app-modal',
  modalSmall: 'app-modal--small',
  modalMedium: 'app-modal--medium',
  modalLarge: 'app-modal--large',
  modalXLarge: 'app-modal--xlarge',
  modalFullscreen: 'app-modal--fullscreen',
  modalAuto: 'app-modal--auto',
  
  // Position classes
  modalCentered: 'app-modal--centered',
  modalTop: 'app-modal--top',
  modalBottom: 'app-modal--bottom',
  modalLeft: 'app-modal--left',
  modalRight: 'app-modal--right',
  
  // Type classes
  modalAlert: 'app-modal--alert',
  modalConfirm: 'app-modal--confirm',
  modalForm: 'app-modal--form',
  modalCustom: 'app-modal--custom',
  modalDrawer: 'app-modal--drawer',
  modalBottomSheet: 'app-modal--bottom-sheet',
  
  // Component classes
  header: 'app-modal-header',
  headerWithIcon: 'app-modal-header--with-icon',
  headerTitle: 'app-modal-header-title',
  headerSubtitle: 'app-modal-header-subtitle',
  closeButton: 'app-modal-close',
  
  body: 'app-modal-body',
  bodyScrollable: 'app-modal-body--scrollable',
  bodyPadded: 'app-modal-body--padded',
  bodyNoPadding: 'app-modal-body--no-padding',
  
  footer: 'app-modal-footer',
  footerActions: 'app-modal-footer-actions',
  footerCentered: 'app-modal-footer--centered',
  footerSpaceBetween: 'app-modal-footer--space-between',
  
  // Animation classes
  animating: 'app-modal--animating',
  entering: 'app-modal--entering',
  entered: 'app-modal--entered',
  exiting: 'app-modal--exiting',
  exited: 'app-modal--exited',
} as const;

/**
 * Modal icon configurations
 */
export const modalIcons = {
  close: '✕',
  success: '✓',
  error: '⚠',
  warning: '⚠',
  info: 'ℹ',
  question: '?',
  loading: '⊙',
} as const;

/**
 * Default modal messages
 */
export const modalMessages = {
  confirmDelete: 'Are you sure you want to delete this item? This action cannot be undone.',
  confirmCancel: 'Are you sure you want to cancel? Any unsaved changes will be lost.',
  confirmSave: 'Do you want to save your changes?',
  alertSuccess: 'Operation completed successfully.',
  alertError: 'An error occurred. Please try again.',
  alertInfo: 'Please note this information.',
  alertWarning: 'Please proceed with caution.',
} as const;

/**
 * Modal variant configurations for quick setup
 */
export const modalVariants = {
  deleteConfirm: {
    type: 'confirm' as const,
    size: 'small' as const,
    overlayVariant: 'default' as const,
    title: 'Confirm Delete',
    message: modalMessages.confirmDelete,
    confirmText: 'Delete',
    cancelText: 'Cancel',
    isDangerous: true,
  },
  saveConfirm: {
    type: 'confirm' as const,
    size: 'small' as const,
    overlayVariant: 'default' as const,
    title: 'Save Changes',
    message: modalMessages.confirmSave,
    confirmText: 'Save',
    cancelText: 'Don\'t Save',
    isDangerous: false,
  },
  successAlert: {
    type: 'alert' as const,
    size: 'small' as const,
    overlayVariant: 'default' as const,
    title: 'Success',
    message: modalMessages.alertSuccess,
    variant: 'success' as const,
  },
  errorAlert: {
    type: 'alert' as const,
    size: 'small' as const,
    overlayVariant: 'default' as const,
    title: 'Error',
    message: modalMessages.alertError,
    variant: 'error' as const,
  },
} as const;

/**
 * Utility function to merge modal configurations
 */
export function mergeModalConfig(
  baseConfig: Partial<ModalConfig>,
  overrides: Partial<ModalConfig>
): ModalConfig {
  return {
    overlay: { ...baseConfig.overlay, ...overrides.overlay },
    container: { ...baseConfig.container, ...overrides.container },
    sizes: { ...baseConfig.sizes, ...overrides.sizes },
    header: { ...baseConfig.header, ...overrides.header },
    body: { ...baseConfig.body, ...overrides.body },
    footer: { ...baseConfig.footer, ...overrides.footer },
    types: { ...baseConfig.types, ...overrides.types },
    animation: { ...baseConfig.animation, ...overrides.animation },
    accessibility: { ...baseConfig.accessibility, ...overrides.accessibility },
    zIndex: { ...baseConfig.zIndex, ...overrides.zIndex },
    buttons: { ...baseConfig.buttons, ...overrides.buttons },
  } as ModalConfig;
}